import React, { useEffect, useRef, useState } from 'react';
import BIRDS from 'vanta/dist/vanta.birds.min';
import * as THREE from 'three';

interface VantaBackgroundProps {
  settings: {
    mouseControls?: boolean;
    touchControls?: boolean;
    gyroControls?: boolean;
    minHeight?: number;
    minWidth?: number;
    scale?: number;
    scaleMobile?: number;
    backgroundColor?: string | number;
    birdSize?: number;
    wingSpan?: number;
    speedLimit?: number;
    separation?: number;
    alignment?: number;
    cohesion?: number;
    quantity?: number;
  } | null;
}

const VantaBackground = React.forwardRef<any, VantaBackgroundProps>(({ settings }, ref) => {
  const [vantaEffect, setVantaEffect] = useState<any>(null);
  const vantaRef = useRef<HTMLDivElement>(null);

  // Expose vanta effect through ref
  React.useImperativeHandle(ref, () => vantaEffect, [vantaEffect]);
  
  // Store the background color in a ref to persist it across renders
  const backgroundColorRef = useRef<string | number>(0x4255a4);
  
  // Initialize the effect
  useEffect(() => {
    // Always use the configured background color or the stored one
    const backgroundColor = settings?.backgroundColor ?? backgroundColorRef.current;
    
    // Store the background color for future use
    backgroundColorRef.current = backgroundColor;
    
    // Initialize effect if it doesn't exist
    if (!vantaEffect && vantaRef.current) {
      console.log('Initializing Vanta effect with background color:', backgroundColor);
      
      const effect = BIRDS({
        el: vantaRef.current,
        THREE: THREE,
        mouseControls: settings?.mouseControls ?? true,
        touchControls: settings?.touchControls ?? true,
        gyroControls: settings?.gyroControls ?? false,
        minHeight: settings?.minHeight ?? 200.00,
        minWidth: settings?.minWidth ?? 200.00,
        scale: settings?.scale ?? 1.00,
        scaleMobile: settings?.scaleMobile ?? 1.00,
        backgroundColor: backgroundColor,
        birdSize: settings?.birdSize ?? 1.00,
        wingSpan: settings?.wingSpan ?? 30.00,
        speedLimit: settings?.speedLimit ?? 5.00,
        separation: settings?.separation ?? 20.00,
        alignment: settings?.alignment ?? 20.00,
        cohesion: settings?.cohesion ?? 20.00,
        quantity: settings?.quantity ?? 5.00
      });
      
      setVantaEffect(effect);
    } 
    // Update existing effect with new settings if it exists
    else if (vantaEffect && settings) {
      console.log('Updating Vanta effect with background color:', backgroundColor);
      
      vantaEffect.setOptions({
        mouseControls: settings.mouseControls,
        touchControls: settings.touchControls,
        gyroControls: settings.gyroControls,
        minHeight: settings.minHeight,
        minWidth: settings.minWidth,
        scale: settings.scale,
        scaleMobile: settings.scaleMobile,
        backgroundColor: backgroundColor,
        birdSize: settings.birdSize,
        wingSpan: settings.wingSpan,
        speedLimit: settings.speedLimit,
        separation: settings.separation,
        alignment: settings.alignment,
        cohesion: settings.cohesion,
        quantity: settings.quantity
      });
    }
    
    // Cleanup function
    return () => {
      if (vantaEffect) {
        vantaEffect.destroy();
      }
    };
  }, [settings, vantaEffect]);
  
  return <div ref={vantaRef} className="absolute inset-0 z-0" aria-hidden="true" />;
});

VantaBackground.displayName = 'VantaBackground';

export default VantaBackground;